<template>
  <div class="page-wrapper">
    <!-- Top Header -->
    <div class="header-section">
      <h1 class="main-title">Users</h1>
      <button class="add-btn" @click="openModal">+ Add User</button>
    </div>
    <!-- Clinic Dropdown -->
    <div class="clinic-dropdown" ref="mainclinicDropdown">
      <input
        type="text"
        v-model="clinicSearch"
        @focus="handleFocus"
        placeholder="Select Clinic"
        class="clinic-input"
      />
        <!-- Magnifying Glass Icon -->
  <span class="clinic-search-icon" @click.stop="handleFocus">
    <svg
      width="18"
      height="18"
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden="true"
      focusable="false"
    >
      <circle cx="11" cy="11" r="7" stroke="currentColor" stroke-width="1.8" />
      <line
        x1="16.65"
        y1="16.65"
        x2="21"
        y2="21"
        stroke="currentColor"
        stroke-width="1.8"
        stroke-linecap="round"
      />
    </svg>
  </span>
      <div v-if="openmain" class="clinic-options">
        <div
          v-for="clinic in filteredClinics"
          :key="clinic.clinicLinkId"
          class="clinic-option"
          @click.stop="selectClinic(clinic)"
        >
          {{ clinic.name }}
        </div>
        <div v-if="filteredClinics.length === 0" class="clinic-option disabled">
          No clinics found
        </div>
      </div>
    </div>

    <!-- Action Buttons for Bulk Operations -->
    <div class="bulk-actions" v-if="selectedClinic">
      <button
        class="bulk-action-btn"
        :disabled="selectedUsers.length === 0"
        @click="openBulkDeleteModal"
      >
        Delete Selected
      </button>
      <button
        class="bulk-action-btn"
        :disabled="selectedUsers.length === 0"
        @click="openBulkResetModal"
      >
        Reset Password
      </button>
      <div class="search-container">
        <input
          type="text"
          v-model="searchQuery"
          placeholder="Search user"
          class="search-input"
        />
        <i class="fas fa-search search-icon"></i>
      </div>
    </div>

    <!-- Table -->
    <div class="table-container" v-if="selectedClinic">
      <table class="styled-table">
        <thead>
          <tr>
            <th>
              <input
                type="checkbox"
                v-model="selectAll"
                @change="toggleSelectAll"
              />
            </th>
            <th>Sr No.</th>
            <th>Email</th>
            <th>Status</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="(user, index) in filteredUsers" :key="user.id">
            <td>
              <input type="checkbox" v-model="selectedUsers" :value="user.id" />
            </td>
            <td>{{ index + 1 }}</td>
            <td>{{ user.email }}</td>
            <td>{{ user.active ? "Active" : "Inactive" }}</td>
            <td>
              <div class = "action-icons">
              <i
                class="fas fa-pen action-icon edit"
                :class="{ disabled: selectedUsers.length > 0 }"
                :title="
                  selectedUsers.length > 0
                    ? 'Disabled in multi-select mode'
                    : 'Edit Password'
                "
                @click="selectedUsers.length > 0 ? null : editUser(user)"
              ></i>
              <i
                class="fas fa-trash action-icon delete"
                :class="{ disabled: selectedUsers.length > 0 }"
                :title="
                  selectedUsers.length > 0
                    ? 'Disabled in multi-select mode'
                    : 'Delete User'
                "
                @click="selectedUsers.length > 0 ? null : deleteUser(user)"
              ></i>
              <i
                class="fas fa-plus action-icon view"
                :class="{ disabled: selectedUsers.length > 0 }"
                title="View User Details"
                @click="selectedUsers.length > 0 ? null : viewUserDetails(user)"
              ></i>
              </div>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <div v-if="showEditModal" class="modal-overlay">
      <div class="modal">
        <h2>Edit Password</h2>
        <form @submit.prevent="updatePassword">
          <div class="form-group">
            <div class="input-wrapper">
              <div v-if="selectedUsers.length > 0" class="m-2">
                <p class="registered-clinics">
                  You are resetting password for the following users:
                </p>
                <div class="registered-clinics-container">
                  <span
                    v-for="(email, idx) in getUsers
                      .filter((u) => selectedUsers.includes(u.id))
                      .map((u) => u.email)"
                    :key="idx"
                    class="registered-clinic-email"
                  >
                    {{ email }}
                  </span>
                </div>
              </div>
              <div v-else class="m-2">
                <p class="registered-clinics">
                  Do you want to reset password for
                  <strong class="registered-clinic-email">{{
                    editUserData.email
                  }}</strong
                  >?
                </p>
              </div>
              <div class="input-wrapper">
                <label class="block mt-4 font-semibold margin"
                  >New Password:</label
                >
                <input
                  :type="showPassword ? 'text' : 'password'"
                  v-model="editUserData.password"
                  placeholder="Enter new password"
                  class="password-input"
                />
                <span class="toggle-password" @click="togglePassword">
                  <i
                    :class="showPassword ? 'fas fa-eye-slash' : 'fas fa-eye'"
                  ></i>
                </span>
              </div>
            </div>
          </div>
          <div class="actions">
            <button
              type="button"
              class="cancel-btn"
              @click="cancelResetPassword"
            >
              Cancel
            </button>
            <button type="submit" class="update-btn" :disabled="isUpdating">
              {{ isUpdating ? "Updating..." : "Update" }}
            </button>
          </div>
        </form>
      </div>
    </div>

    <div v-if="showDeleteModal" class="modal-overlay">
      <div class="modal">
        <h2>Delete Users</h2>
        <div class="form-group">
          <div class="input-wrapper">
            <div v-if="selectedUsers.length > 0">
              <p class="registered-clinics">
                You are going to delete the following users from the clinic:
              </p>

              <div class="registered-clinics-container">
                <span
                  v-for="(email, idx) in getUsers
                    .filter((u) => selectedUsers.includes(u.id))
                    .map((u) => u.email)"
                  :key="idx"
                  class="registered-clinic-email"
                >
                  {{ email }}
                </span>
              </div>
            </div>

            <div v-else>
              <p class="registered-clinics">
                You are going to delete the following users from the clinic:
                <strong class="registered-clinic-email">{{
                  deleteUserData[0]?.email
                }}</strong>
              </p>
            </div>
          </div>
        </div>

        <div class="actions">
          <button type="button" class="cancel-btn" @click="cancelDeleteUser">
            Cancel
          </button>
          <button
            type="submit"
            class="delete-btn"
            :disabled="isDeleting"
            @click="confirmDelete"
          >
            {{ isDeleting ? "Deleting..." : "Delete" }}
          </button>
        </div>
      </div>
    </div>
    <!-- User Details Modal -->
    <div v-if="showUserDetailsModal" class="modal-overlay">
      <div class="modal" ref="addUserModal">
        <button class="modal-close-btn" @click="closeUserDetailsModal">
          ✕
        </button>
        <h2>User Details</h2>
        <div class="user-details-section">
          <div class="detail-row">
            <span class="detail-label">Email:</span>
            <span class="detail-value">{{ userDetails.email }}</span>
          </div>
          <div class="detail-row">
            <span class="detail-label">Clinic:</span>
            <span class="detail-value">{{ selectedClinicLabel }}</span>
          </div>
        </div>

        <!-- Categories and Roles Section -->
        <div class="categories-roles-section">
          <h3>Categories & Roles</h3>

          <div
            v-for="(categoryData, index) in userDetailsCategoryRoles"
            :key="index"
            class="category-roles-section"
          >
            <!-- Cross icon to delete entire category box -->
            <i
              class="fas fa-times delete-category-icon"
              @click="deleteUserDetailsCategoryBox(index)"
            ></i>
            <!-- Category Dropdown -->
            <div class="category-form-group">
              <label>Category {{ index + 1 }}:</label>
              <div
                class="category-dropdown"
                @click="toggleUserDetailsCategoryDropdown(index)"
                :ref="`userDetailsCategoryDropdown-${index}`"
              >
                <div class="category-selected">
                  {{
                    categoryData.category
                      ? categoryData.category.name
                      : "Select Category"
                  }}
                  <i
                    :class="
                      categoryData.open
                        ? 'fas fa-chevron-up'
                        : 'fas fa-chevron-down'
                    "
                  ></i>
                </div>
                <div v-if="categoryData.open" class="category-options">
                  <div
                    v-for="category in getUserDetailsAvailableCategories(index)"
                    :key="category.id"
                    class="category-option"
                    @click.stop="selectUserDetailsCategory(category, index)"
                  >
                    {{ category.name }}
                  </div>
                </div>
              </div>

              <!-- Selected Category Display -->
              <div v-if="categoryData.category" class="selected-tags-container">
                <span class="selected-tag">
                  {{ categoryData.category.name }}
                  <i
                    class="fas fa-times remove-tag"
                    @click="removeUserDetailsCategory(index)"
                  ></i>
                </span>
              </div>
            </div>

            <!-- Roles Dropdown (only enabled when category is selected) -->
            <div class="roles-form-group" v-if="categoryData.category">
              <label>Roles for Category {{ index + 1 }}:</label>
              <div
                class="roles-dropdown"
                @click="toggleUserDetailsRolesDropdown(index)"
                :ref="`userDetailsRolesDropdown-${index}`"
              >
                <div class="roles-selected">
                  {{
                    categoryData.roles.length > 0
                      ? `${categoryData.roles.length} roles selected`
                      : "Select Roles"
                  }}
                  <i
                    :class="
                      categoryData.rolesOpen
                        ? 'fas fa-chevron-up'
                        : 'fas fa-chevron-down'
                    "
                  ></i>
                </div>
                <div v-if="categoryData.rolesOpen" class="roles-options">
                  <div
                    v-for="role in getRolesByCategory(categoryData.category.id)"
                    :key="role.id"
                    class="roles-option"
                    @click.stop="toggleUserDetailsRole(role, index)"
                  >
                    <span class="role-name">{{ role.name }}</span>
                    <input
                      type="checkbox"
                      :checked="isUserDetailsRoleSelected(role.id, index)"
                      class="role-checkbox"
                    />
                  </div>
                </div>
              </div>

              <!-- Selected Roles Display -->
              <div
                v-if="categoryData.roles.length > 0"
                class="selected-tags-container"
              >
                <span
                  v-for="role in categoryData.roles"
                  :key="role.id"
                  class="selected-tag"
                >
                  {{ role.name }}
                  <i
                    class="fas fa-times remove-tag"
                    @click="removeUserDetailsRole(role, index)"
                  ></i>
                </span>
              </div>
            </div>

            <!-- Add Category Button (show after roles are selected and if not the last category) -->
            <div
              v-if="
                categoryData.roles.length > 0 &&
                index === userDetailsCategoryRoles.length - 1 &&
                getUserDetailsAvailableCategories(index).length - 1 > 0
              "
              class="add-category-btn-container"
            >
              <button
                type="button"
                class="add-category-btn"
                @click="addNewUserDetailsCategory"
              >
                + Add Category {{ userDetailsCategoryRoles.length + 1 }}
              </button>
            </div>
          </div>
        </div>

        <div class="actions">
          <button
            type="button"
            class="cancel-btn"
            @click="closeUserDetailsModal"
          >
            Close
          </button>
          <button
            type="button"
            class="save-btn"
            :disabled="!hasUserDetailsChanged"
            @click="updateUserCategoriesRoles"
          >
            Save Changes
          </button>
        </div>
      </div>
    </div>
    <!-- Modal -->
    <div v-if="showModal" class="modal-overlay">
      <div class="modal" ref="addUserModal">
        <h2>Add User</h2>

        <!-- Tabs -->
        <div class="tabs">
          <button
            :class="{ active: activeTab === 'form' }"
            @click="activeTab = 'form'"
          >
            Single User
          </button>
          <button
            :class="{ active: activeTab === 'csv' }"
            @click="activeTab = 'csv'"
          >
            File Upload
          </button>
        </div>

        <!-- Form for single user -->
        <form v-if="activeTab === 'form'" @submit.prevent="saveUser">
          <div
            v-if="
              showAlreadyRegisteredEmailClinics &&
              listAlreadyRegisteredEmailClinics.length > 0
            "
            class="registered-email-clinics-section"
          >
            <div
              class="registered-email-clinics-header"
              @click="showClinicsList = !showClinicsList"
            >
              <i class="fa-solid fa-triangle-exclamation warning-icon"></i>
              <span class="toggle-text">
                {{
                  showClinicsList
                    ? "Hide Registered Clinics"
                    : "View Registered Clinics (" +
                      listAlreadyRegisteredEmailClinics.length +
                      ")"
                }}
              </span>
            </div>

            <transition name="fade">
              <div v-if="showClinicsList" class="registered-clinics-list-box">
                <p class="registered-clinics">
                  This email is already registered under the following clinics:
                </p>

                <div class="registered-clinics-container">
                  <span
                    v-for="(clinic, idx) in listAlreadyRegisteredEmailClinics"
                    :key="idx"
                    class="registered-clinic-email"
                  >
                    {{ clinic }}
                  </span>
                </div>
              </div>
            </transition>
          </div>

          <div class="filter-section">
            <div
              class="clinic-dropdown"
              @click="open = !open"
              ref="clinicDropdown"
            >
              <div class="clinic-selected">
                {{ selectedClinicLabel || "Select Clinic" }}
              </div>
              <div v-if="open" class="clinic-options">
                <div
                  v-for="clinic in getClinics"
                  :key="clinic.clinicLinkId"
                  class="clinic-option"
                  @click.stop="selectClinic(clinic)"
                >
                  {{ clinic.name }}
                </div>
              </div>
            </div>
          </div>
          <div class="form-group">
            <div class="input-wrapper">
              <label>Email:</label>
              <input
                type="email"
                v-model="newUser.email"
                class="password-input"
              />
              <span class="email">
                <i class="icon fas fa-envelope"></i>
              </span>
            </div>
            <p v-if="emailExistsInSameClinic" class="info-text error-msg">
              This user is already present in the selected clinic.
            </p>

            <!-- <p v-else-if="emailExists && !emailExistsInSameClinic" class="info-text warning-msg">
              This email already exists in another clinic. The existing password will remain unchanged.
            </p> -->
            <p
              v-else-if="emailExists && !emailExistsInSameClinic"
              class="info-text warning-msg"
            >
              This email already exists in another clinic. The existing password
              will remain unchanged. or You can reset Password below.
            </p>
            <div
              class="input-wrapper"
              v-if="emailExists && !emailExistsInSameClinic"
            >
              <label>Reset Password:</label>
              <input
                :type="showPassword ? 'text' : 'password'"
                v-model="newUser.resetPassword"
                placeholder="Enter New Password"
                class="password-input"
              />
              <span class="toggle-password" @click="togglePassword">
                <i
                  :class="showPassword ? 'fas fa-eye-slash' : 'fas fa-eye'"
                ></i>
              </span>
            </div>
          </div>
          <div class="form-group" v-if="!emailExists && newUser.email">
            <div class="input-wrapper">
              <label>Password:</label>
              <input
                :type="showPassword ? 'text' : 'password'"
                v-model="newUser.password"
                placeholder="Leave blank to set default Password"
                class="password-input"
              />
              <span class="toggle-password" @click="togglePassword">
                <i
                  :class="showPassword ? 'fas fa-eye-slash' : 'fas fa-eye'"
                ></i>
              </span>
            </div>
          </div>
          <!-- Category and Roles Sections -->
          <div v-if="!emailExistsInSameClinic">
            <div
              v-for="(categoryData, index) in categoryRoles"
              :key="index"
              class="category-roles-section"
            >
              <i
                class="fas fa-times delete-category-icon"
                @click="deleteCategoryBox(index)"
              ></i>
              <!-- Category Dropdown -->
              <div class="category-form-group">
                <label>Category {{ index + 1 }}:</label>
                <div
                  class="category-dropdown"
                  @click="toggleCategoryDropdown(index)"
                  :ref="`categoryDropdown-${index}`"
                >
                  <div class="category-selected">
                    {{
                      categoryData.category
                        ? categoryData.category.name
                        : "Select Category"
                    }}
                    <i
                      :class="
                        categoryData.open
                          ? 'fas fa-chevron-up'
                          : 'fas fa-chevron-down'
                      "
                    ></i>
                  </div>
                  <div v-if="categoryData.open" class="category-options">
                    <div
                      v-for="category in getAvailableCategories(index)"
                      :key="category.id"
                      class="category-option"
                      @click.stop="selectCategory(category, index)"
                    >
                      {{ category.name }}
                    </div>
                  </div>
                </div>

                <!-- Selected Category Display -->
                <div
                  v-if="categoryData.category"
                  class="selected-tags-container"
                >
                  <span class="selected-tag">
                    {{ categoryData.category.name }}
                    <i
                      class="fas fa-times remove-tag"
                      @click="removeCategory(index)"
                    ></i>
                  </span>
                </div>
              </div>

              <!-- Roles Dropdown (only enabled when category is selected) -->
              <div class="roles-form-group" v-if="categoryData.category">
                <label>Roles for Category {{ index + 1 }}:</label>
                <div
                  class="roles-dropdown"
                  @click="toggleRolesDropdown(index)"
                  :ref="`rolesDropdown-${index}`"
                >
                  <div class="roles-selected">
                    {{
                      categoryData.roles.length > 0
                        ? `${categoryData.roles.length} roles selected`
                        : "Select Roles"
                    }}
                    <i
                      :class="
                        categoryData.rolesOpen
                          ? 'fas fa-chevron-up'
                          : 'fas fa-chevron-down'
                      "
                    ></i>
                  </div>
                  <div v-if="categoryData.rolesOpen" class="roles-options">
                    <div
                      v-for="role in getRolesByCategory(
                        categoryData.category.id
                      )"
                      :key="role.id"
                      class="roles-option"
                      @click.stop="toggleRole(role, index)"
                    >
                      <span class="role-name">{{ role.name }}</span>
                      <input
                        type="checkbox"
                        :checked="isRoleSelected(role.id, index)"
                        class="role-checkbox"
                      />
                    </div>
                  </div>
                </div>

                <!-- Selected Roles Display -->
                <div
                  v-if="categoryData.roles.length > 0"
                  class="selected-tags-container"
                >
                  <span
                    v-for="role in categoryData.roles"
                    :key="role.id"
                    class="selected-tag"
                  >
                    {{ role.name }}
                    <i
                      class="fas fa-times remove-tag"
                      @click="removeRole(role, index)"
                    ></i>
                  </span>
                </div>
              </div>

              <!-- Add Category Button (show after roles are selected and if not the last category) -->
              <div
                v-if="
                  categoryData.roles.length > 0 &&
                  index === categoryRoles.length - 1 &&
                  getAvailableCategories(index).length - 1 > 0
                "
                class="add-category-btn-container"
              >
                <button
                  type="button"
                  class="add-category-btn"
                  @click="addNewCategory"
                >
                  + Add Category {{ categoryRoles.length + 1 }}
                </button>
              </div>
            </div>
          </div>

<div class="actions">

  <!-- Left side -->
  <label class="department-checkbox">
    <input type="checkbox" v-model="isDepartmentUser" />
    Department User
  </label>

  <!-- Right side buttons -->
  <div class="action-buttons">
    <button type="button" class="cancel-btn" @click="cancel">
      Cancel
    </button>

    <button
      type="submit"
      class="save-btn"
      :disabled="isSaving || userSaved"
    >
      {{ isSaving ? "Saving..." : "Save" }}
    </button>
  </div>

</div>

        </form>

        <!-- CSV Upload -->
        <form
          v-if="activeTab === 'csv'"
          class="csv-upload"
          @submit.prevent="saveUserByExcel"
        >
          <div
            v-if="alreadyExistingWithSameClinicEmails.length > 0"
            class="same-clinic-emails-section"
          >
            <div
              class="same-clinic-header"
              @click="showSameClinicEmails = !showSameClinicEmails"
            >
              <i class="fa-solid fa-circle-exclamation warning-icon"></i>
              <span class="toggle-text">
                {{
                  showSameClinicEmails
                    ? "Hide Same Clinic Emails"
                    : "View Same Clinic Emails (" +
                      alreadyExistingWithSameClinicEmails.length +
                      ")"
                }}
              </span>
            </div>

            <transition name="fade">
              <div v-if="showSameClinicEmails" class="same-clinic-list-box">
                <p class="same-clinic-subtitle">
                  These users cannot be saved because they already exist with
                  the same email and clinic:
                </p>
                <div class="same-clinic-emails-container">
                  <span
                    v-for="(
                      email, index
                    ) in alreadyExistingWithSameClinicEmails"
                    :key="index"
                    class="same-clinic-email-chip"
                  >
                    {{ email }}
                  </span>
                </div>
              </div>
            </transition>
          </div>

          <!-- Existing Emails Section -->
          <div v-if="existingEmails.length > 0" class="existing-emails-section">
            <div
              class="existing-header"
              @click="showExistingEmails = !showExistingEmails"
            >
              <i class="fa-solid fa-circle-info info-icon"></i>
              <span class="toggle-text">
                {{
                  showExistingEmails
                    ? "Hide Existing Emails"
                    : "View Existing Emails (" + existingEmails.length + ")"
                }}
              </span>
            </div>

            <transition name="fade">
              <div v-if="showExistingEmails" class="existing-list-box">
                <p class="existing-subtitle">
                  These emails already exist and their password was retained:
                </p>
                <div class="existing-emails-container">
                  <span
                    v-for="(email, index) in existingEmails"
                    :key="index"
                    class="existing-email-chip"
                  >
                    {{ email }}
                  </span>
                </div>
              </div>
            </transition>
          </div>
          <br />
          <div v-if="invalidEmails.length > 0" class="invalid-emails-section">
            <div
              class="invalid-header"
              @click="showInvalidEmails = !showInvalidEmails"
            >
              <i class="fa-solid fa-circle-info info-icon"></i>
              <span class="toggle-text">
                {{
                  showInvalidEmails
                    ? "Hide Invalid Emails"
                    : "View Invalid Emails (" + invalidEmails.length + ")"
                }}
              </span>
            </div>

            <transition name="fade">
              <div v-if="showInvalidEmails" class="invalid-list-box">
                <p class="invalid-subtitle">
                  The following emails are invalid:
                </p>
                <div class="invalid-emails-container">
                  <span
                    v-for="(email, index) in invalidEmails"
                    :key="index"
                    class="invalid-email-chip"
                  >
                    {{ email }}
                  </span>
                </div>
              </div>
            </transition>
          </div>
          <div class="filter-section">
            <div
              class="clinic-dropdown"
              @click="open = !open"
              ref="clinicDropdown"
            >
              <div class="clinic-selected">
                {{ selectedClinicLabel || "Select Clinic" }}
              </div>
              <div v-if="open" class="clinic-options">
                <div
                  v-for="clinic in getClinics"
                  :key="clinic.clinicLinkId"
                  class="clinic-option"
                  @click.stop="selectClinic(clinic)"
                >
                  {{ clinic.name }}
                </div>
              </div>
            </div>
          </div>
          <p><strong>Upload a CSV/Excel file</strong></p>
          <input
            type="file"
            accept=".csv,.xls,.xlsx"
            @change.prevent="handleCSVUpload"
            ref="csvInput"
            required
          />
          <div class="form-group">
            <br />
            <div class="input-wrapper">
              <label>Password:</label>
              <input
                :type="showPassword ? 'text' : 'password'"
                v-model="bulkUser.password"
                placeholder="Leave blank to set default Password"
                class="password-input"
              />
              <span class="toggle-password" @click="togglePassword">
                <i
                  :class="showPassword ? 'fas fa-eye-slash' : 'fas fa-eye'"
                ></i>
              </span>
            </div>
          </div>
          <!-- ADD THE CATEGORY AND ROLES SECTION HERE -->
          <div
            v-for="(categoryData, index) in csvCategoryRoles"
            :key="index"
            class="category-roles-section"
          >
            <i
              class="fas fa-times delete-category-icon"
              @click="deleteCsvCategoryBox(index)"
            ></i>

            <!-- Category Dropdown -->
            <div class="category-form-group">
              <label>Category {{ index + 1 }}:</label>
              <div
                class="category-dropdown"
                @click="toggleCsvCategoryDropdown(index)"
                :ref="`csvCategoryDropdown-${index}`"
              >
                <div class="category-selected">
                  {{
                    categoryData.category
                      ? categoryData.category.name
                      : "Select Category"
                  }}
                  <i
                    :class="
                      categoryData.open
                        ? 'fas fa-chevron-up'
                        : 'fas fa-chevron-down'
                    "
                  ></i>
                </div>
                <div v-if="categoryData.open" class="category-options">
                  <div
                    v-for="category in getCsvAvailableCategories(index)"
                    :key="category.id"
                    class="category-option"
                    @click.stop="selectCsvCategory(category, index)"
                  >
                    {{ category.name }}
                  </div>
                </div>
              </div>

              <!-- Selected Category Display -->
              <div v-if="categoryData.category" class="selected-tags-container">
                <span class="selected-tag">
                  {{ categoryData.category.name }}
                  <i
                    class="fas fa-times remove-tag"
                    @click="removeCsvCategory(index)"
                  ></i>
                </span>
              </div>
            </div>

            <!-- Roles Dropdown (only enabled when category is selected) -->
            <div class="roles-form-group" v-if="categoryData.category">
              <label>Roles for Category {{ index + 1 }}:</label>
              <div
                class="roles-dropdown"
                @click="toggleCsvRolesDropdown(index)"
                :ref="`csvRolesDropdown-${index}`"
              >
                <div class="roles-selected">
                  {{
                    categoryData.roles.length > 0
                      ? `${categoryData.roles.length} roles selected`
                      : "Select Roles"
                  }}
                  <i
                    :class="
                      categoryData.rolesOpen
                        ? 'fas fa-chevron-up'
                        : 'fas fa-chevron-down'
                    "
                  ></i>
                </div>
                <div v-if="categoryData.rolesOpen" class="roles-options">
                  <div
                    v-for="role in getRolesByCategory(categoryData.category.id)"
                    :key="role.id"
                    class="roles-option"
                    @click.stop="toggleCsvRole(role, index)"
                  >
                    <span class="role-name">{{ role.name }}</span>
                    <input
                      type="checkbox"
                      :checked="isCsvRoleSelected(role.id, index)"
                      class="role-checkbox"
                    />
                  </div>
                </div>
              </div>

              <!-- Selected Roles Display -->
              <div
                v-if="categoryData.roles.length > 0"
                class="selected-tags-container"
              >
                <span
                  v-for="role in categoryData.roles"
                  :key="role.id"
                  class="selected-tag"
                >
                  {{ role.name }}
                  <i
                    class="fas fa-times remove-tag"
                    @click="removeCsvRole(role, index)"
                  ></i>
                </span>
              </div>
            </div>

            <!-- Add Category Button -->
            <div
              v-if="
                categoryData.roles.length > 0 &&
                index === csvCategoryRoles.length - 1 &&
                getCsvAvailableCategories(index).length - 1 > 0
              "
              class="add-category-btn-container"
            >
              <button
                type="button"
                class="add-category-btn"
                @click="addNewCsvCategory"
              >
                + Add Category {{ csvCategoryRoles.length + 1 }}
              </button>
            </div>
          </div>
          <!-- END OF CATEGORY AND ROLES SECTION -->
          <div class="actions">

  <!-- Left side -->
  <label class="department-checkbox">
    <input type="checkbox" v-model="isDepartmentUserFileUpload" />
    Department User
  </label>

  <!-- Right side buttons -->
  <div class="action-buttons">
    <button type="button" class="cancel-btn" @click="cancel">
      Cancel
    </button>

    <button
      type="submit"
      class="save-btn"
      :disabled="isSaving || userSaved"
    >
      {{ isSaving ? "Saving..." : "Save" }}
    </button>
  </div>

</div>


        </form>
      </div>
    </div>
    <div v-if="showConfirmationModal" class="modal-overlay">
      <div class="modal">
        <h2>Confirm Deletion</h2>
        <p>{{ confirmationMessage }}</p>
        <div class="actions">
          <button type="button" class="cancel-btn" @click="cancelDeletion">
            Cancel
          </button>
          <button type="button" class="save-btn" @click="confirmDeletion">
            Yes, Delete
          </button>
        </div>
      </div>
    </div>
    <!-- Popup Notification -->
    <div class="popup-modal">
      <div v-if="popupMessage" :class="['popup', popupType]">
        {{ popupMessage }}
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters, mapActions } from "vuex";

export default {
  name: "UsersPage",
  mounted() {
    this.$store
      .dispatch("fetchClinics")
      .then(() => {
        return this.$store.dispatch("fetchAllUsers");
      })
      .then(() => {
        return this.$store.dispatch("fetchCategories");
      })
      .then(() => {
        // Find the Healthcare category and Staff role
        const healthcareCategory = this.categories.find(cat => 
          cat.name.toLowerCase() === 'healthcare'
        );
        
        let staffRole = null;
        if (healthcareCategory) {
          staffRole = healthcareCategory.roles.find(role => 
            role.name.toLowerCase() === 'staff'
          );
        }

        // Initialize with default category and role if available
        if (healthcareCategory) {
          this.categoryRoles = [
            {
              category: healthcareCategory,
              roles: staffRole ? [staffRole] : [],
              open: false,
              rolesOpen: false,
            },
          ];
          this.csvCategoryRoles = [
            {
              category: healthcareCategory,
              roles: staffRole ? [staffRole] : [],
              open: false,
              rolesOpen: false,
            },
          ];
        } else {
          // Fallback to empty if Healthcare category not found
          this.categoryRoles = [
            {
              category: null,
              roles: [],
              open: false,
              rolesOpen: false,
            },
          ];
          this.csvCategoryRoles = [
            {
              category: null,
              roles: [],
              open: false,
              rolesOpen: false,
            },
          ];
        }
      });

    this.$store.commit("SET_USERS", []);
    document.addEventListener("click", this.handleClickOutside);
  },
  beforeUnmount() {
    document.removeEventListener("click", this.handleClickOutside);
  },
  data() {
    return {
      showEditModal: false,
      showDeleteModal: false,
      deleteUserData: {
        linkId: "",
        email: "",
      },
      editUserData: {
        email: "",
        password: "",
        linkId: "",
        clinicName: "",
      },
      selectedClinic: "",
      clinicSearch: "",
      showModal: false,
      activeTab: "form",
      newUser: {
        email: "",
        password: "",
        status: "Active",
        clinic: "",
        reset: false,
      },
      bulkUser: {
        password: "",
        clinic: "",
        csvFileName: "",
      },
      popupMessage: "",
      popupType: "",
      showPassword: false,
      openmain: false,
      open: false,
      selectedUsers: [],
      selectAll: false,
      invalidEmails: [],
      showInvalidEmails: false,
      emailExists: false,
      inSameClinic: false,
      existingEmails: [],
      showExistingEmails: false,
      alreadyExistingWithSameClinicEmails: [],
      showSameClinicEmails: false,
      emailExistsInSameClinic: false,
      isBulkUpdate: false,
      listAlreadyRegisteredEmailClinics: {},
      showAlreadyRegisteredEmailClinics: false,
      showClinicsList: true,
      isSaving: false,
      isUpdating: false,
      isDeleting: false,
      userSaved: false,
      csvCategoryRoles: [],
      selectedCategory: null,
      selectedRoles: [],
      categoryOpen: false,
      rolesOpen: false,
      categoryRoles: [],
      showUserDetailsModal: false,
      userDetails: {
        email: "",
        linkId: "",
      },
      userDetailsCategoryRoles: [],
      showConfirmationModal: false,
      confirmationMessage: "",
      deletionContext: null, // 'userDetails' or 'addUser'
      deletionIndex: null,
      originalUserDetailsCategoryRoles: [],
      hasUserDetailsChanged: false,
      searchQuery: "",
      isDepartmentUser: true, // default checked
      isDepartmentUserFileUpload: true, // default checked
    };
  },
  computed: {
    ...mapGetters(["getClinics", "getUsers", "getAllUsers"]),
    categories() {
      return this.$store.state.categories;
    },
    usersBySelectedClinic() {
      return this.$store.state.usersByLink[this.newUser.clinic] || [];
    },
    selectedClinicLabel() {
      const c = this.getClinics.find(
        (x) => x.clinicLinkId === this.selectedClinic
      );
      return c ? c.name : "";
    },
    filteredClinics() {
      if (!this.clinicSearch) return this.getClinics;
      const search = (this.clinicSearch || "")
        .toLowerCase()
        .replace(/\s+/g, "");
      return this.getClinics.filter((c) => {
        const name = (c?.name || "").toLowerCase().replace(/\s+/g, "");
        
        return name.includes(search);
      });
    },
    selectedCategoryName() {
      return this.selectedCategory ? this.selectedCategory.name : "";
    },

    filteredRoles() {
      if (!this.selectedCategory) return [];
      return this.roles.filter(
        (role) => role.categoryId === this.selectedCategory.id
      );
    },
    getAvailableCategories() {
      return (index) => {
        // Get all selected category IDs except the current one
        const selectedCategoryIds = this.categoryRoles
          .map((cr, i) => (i !== index ? cr.category?.id : null))
          .filter((id) => id !== null && id !== undefined);

        // Return categories that are not already selected
        return this.categories.filter(
          (category) => !selectedCategoryIds.includes(category.id)
        );
      };
    },
    filteredUsers() {
      if (!this.searchQuery) {
        return this.getUsers;
      }

      const query = this.searchQuery.toLowerCase();
      return this.getUsers.filter((user) =>
        user.email.toLowerCase().includes(query)
      );
    },
    hasDefaultCategory() {
      return this.categories.some(cat => cat.name.toLowerCase() === 'healthcare');
    },
  },
  methods: {
    ...mapActions(["fetchUsersByClinic"]),
    handleFocus() {
      this.clinicSearch = "";
      this.openmain = true;
      this.$store.commit("SET_USERS", []);
      this.selectedClinic = null;
      this.searchQuery = "";
    },

    cancelDeleteUser() {
      this.deleteUserData = { linkId: "", email: "" };
      this.showDeleteModal = false;
      this.selectedUsers = [];
      this.selectAll = false;
    },

    cancelResetPassword() {
      this.editUserData = {
        email: "",
        password: "",
        linkId: "",
        clinicName: "",
      };
      this.showEditModal = false;
      this.selectedUsers = [];
      this.selectAll = false;
    },

    cancel() {
      this.showModal = false;
      this.invalidEmails = [];
      this.showInvalidEmails = false;
      this.existingEmails = [];
      this.showExistingEmails = false;
      this.alreadyExistingWithSameClinicEmails = [];
      this.showSameClinicEmails = false;
      this.bulkUser = { csvFile: "", password: "", clinic: "" };
      this.userSaved = false;
      this.newUser = { email: "", password: "", clinic: "" };
      this.showModal = false;
      this.emailExists = false;
      this.emailExistsInSameClinic = false;
      
      // Reset to default category and roles instead of empty
      this.setDefaultCategoryRoles();
    },

    showPopup(message, type = "success") {
      this.popupMessage = message;
      this.popupType = type;
      setTimeout(() => {
        this.popupMessage = "";
        this.popupType = "";
      }, 6000);
    },

    editUser(user) {
      this.editUserData = {
        email: user.email,
        password: "",
        linkId: this.selectedClinic.clinicLinkId,
        clinicName: this.selectedClinic.name,
      };
      this.showEditModal = true;
    },

    async updatePassword() {
      if (this.isUpdating) return;
      this.isUpdating = true;
      if (this.editUserData.password === "") {
        this.showPopup("Password must not be empty", "error");
        this.isUpdating = false;
        return;
      }
      if (
        !this.editUserData.password ||
        this.editUserData.password.length < 6
      ) {
        this.showPopup("Password must be at least 6 characters long.", "error");
        this.isUpdating = false;
        return;
      }

      const emails =
        this.selectedUsers.length > 0
          ? this.getUsers
              .filter((u) => this.selectedUsers.includes(u.id))
              .map((u) => u.email)
          : [this.editUserData.email];

      const payload = {
        emails,
        password: this.editUserData.password,
      };

      try {
        await this.$store.dispatch("updateUserPassword", payload);
        this.isUpdating = false;
        this.showPopup("Password updated successfully", "success");
        this.showEditModal = false;
        this.selectedUsers = [];
        await this.$store.dispatch("fetchAllUsers");
        if (this.selectedClinic) {
          await this.fetchUsersByClinic(this.selectedClinic);
        }

        // this.showEditModal = false;
        // this.selectedUsers = [];
        this.selectAll = false;
      } catch (err) {
        console.error("Update password failed", err);
        const backendMessage =
          err.response?.data?.message || "Password update failed. Try again.";
        this.showPopup(backendMessage, "error");
      } finally {
        this.isUpdating = false;
      }
    },

    openModal() {
      this.newUser.clinic = this.selectedClinic || "";
      this.bulkUser.clinic = this.selectedClinic || "";
      this.showModal = true;
      
      // Reset to default Healthcare category and Staff role
      this.setDefaultCategoryRoles();
      
      setTimeout(() => {
        this.scrollToBottom();
      }, 100);
    },
    // Add this method to set default category and role
    setDefaultCategoryRoles() {
      // Find the Healthcare category and Staff role
      const healthcareCategory = this.categories.find(cat => 
        cat.name.toLowerCase() === 'healthcare'
      );
      
      let staffRole = null;
      if (healthcareCategory) {
        staffRole = healthcareCategory.roles.find(role => 
          role.name.toLowerCase() === 'staff'
        );
      }

      // Set default for single user form
      if (healthcareCategory) {
        this.categoryRoles = [
          {
            category: healthcareCategory,
            roles: staffRole ? [staffRole] : [],
            open: false,
            rolesOpen: false,
          },
        ];
      } else {
        this.categoryRoles = [
          {
            category: null,
            roles: [],
            open: false,
            rolesOpen: false,
          },
        ];
      }

      // Set default for CSV upload
      if (healthcareCategory) {
        this.csvCategoryRoles = [
          {
            category: healthcareCategory,
            roles: staffRole ? [staffRole] : [],
            open: false,
            rolesOpen: false,
          },
        ];
      } else {
        this.csvCategoryRoles = [
          {
            category: null,
            roles: [],
            open: false,
            rolesOpen: false,
          },
        ];
      }
    },

    async saveUserByExcel() {
      this.userSaved = false;
      if (this.isSaving) return;
      if (!this.bulkUser.clinic) {
        this.showPopup("Please select a clinic", "error");
        return;
      }
      
      // Validate categories and roles for CSV upload
      const hasSelectedCategory = this.csvCategoryRoles.some(
        (cr) => cr.category !== null
      );
      if (!hasSelectedCategory) {
        this.showPopup("Please select at least one category", "error");
        return;
      }

      for (const cr of this.csvCategoryRoles) {
        if (cr.category && cr.roles.length === 0) {
          this.showPopup(
            `Please select at least one role for ${cr.category.name}`,
            "error"
          );
          return;
        }
      }
      
      // Rest of the method remains the same...
      const clinic = this.getClinics.find(
        (c) => c.clinicLinkId === this.bulkUser.clinic
      );

      let passwordValue = this.bulkUser.password;
      if (this.bulkUser.password === "NOT_PROVIDED") {
        this.showPopup("Password cannot be NOT_PROVIDED", "error");
        return;
      }
      if (!passwordValue || passwordValue.trim() === "") {
        passwordValue = "NOT_PROVIDED";
      } else if (passwordValue.length < 6) {
        this.showPopup("Password must be at least 6 characters long.", "error");
        return;
      }
      const mappings = this.csvCategoryRoles.filter((cr) => cr.category !== null).flatMap((cr) => cr.roles);
      const payload = {
        link: this.bulkUser.clinic.trim(),
        clinicName: clinic ? clinic.name : null,
        password: passwordValue,
        csvFile: this.bulkUser.csvFile,
        mappings: mappings,
        isDepartment: this.isDepartmentUserFileUpload,
      };
      console.log("bulk payload", payload);

      try {
        this.isSaving = true;
        const response = await this.$store.dispatch("uploadCSV", payload);

        this.invalidEmails = response.invalidEmails || [];
        this.existingEmails = response.existingEmails || [];
        this.alreadyExistingWithSameClinicEmails =
          response.alreadyExistingWithSameClinicEmails || [];

        let successMsg = "Users added successfully!";
        if (
          this.existingEmails.length > 0 &&
          this.alreadyExistingWithSameClinicEmails.length > 0
        ) {
          successMsg =
            "Users added successfully! Click the info icon to view details.";
        } else {
          if (this.existingEmails.length > 0) {
            successMsg +=
              "Some emails already exist. " +
              "Click the info icon to view details.";
          }

          if (this.alreadyExistingWithSameClinicEmails.length > 0) {
            successMsg +=
              "Some users already exist with the same clinic. " +
              "Click the info icon to view details.";
          }
        }
        this.isSaving = false;
        this.showPopup(successMsg, "success");
        this.userSaved = true;
        await this.$store.dispatch("fetchAllUsers");

        if (this.selectedClinic) {
          await this.fetchUsersByClinic(this.selectedClinic);
        }

        if (
          this.invalidEmails.length === 0 &&
          this.existingEmails.length === 0 &&
          this.alreadyExistingWithSameClinicEmails.length === 0
        ) {
          this.bulkUser = { csvFile: "", password: "", clinic: "" };
          // Reset CSV category roles
          this.csvCategoryRoles = [
            {
              category: null,
              roles: [],
              open: false,
              rolesOpen: false,
            },
          ];
          this.showModal = false;
          this.showInvalidEmails = false;
          this.showExistingEmails = false;
        }
      } catch (err) {
        console.error("Upload failed:", err);

        this.invalidEmails = err.invalidEmails || [];
        this.existingEmails = err.existingEmails || [];
        this.alreadyExistingWithSameClinicEmails =
          err.alreadyExistingWithSameClinicEmails || [];

        let errorMessage = err.message || "Failed to add users!";
        if (this.existingEmails.length > 0) {
          errorMessage +=
            " Some emails already exist, their old password has been retained. " +
            "Click the info icon to view details.";
        }
        this.showPopup(errorMessage, "error");
        this.userSaved = false;
      } finally {
        this.isSaving = false;
      }
    },

    async saveUser() {
      if (this.isSaving) return;
      if (!this.newUser.clinic) {
        this.showPopup("Please select a clinic", "error");
        return;
      }
      if (!this.newUser.email) {
        this.showPopup("Please enter your email", "error");
        return;
      }
      
      // Validate categories and roles
      const hasSelectedCategory = this.categoryRoles.some(
        (cr) => cr.category !== null
      );
      if (!hasSelectedCategory) {
        this.showPopup("Please select at least one category", "error");
        return;
      }

      for (const cr of this.categoryRoles) {
        if (cr.category && cr.roles.length === 0) {
          this.showPopup(
            `Please select at least one role for ${cr.category.name}`,
            "error"
          );
          return;
        }
      }
      const clinic = this.getClinics.find(
        (c) => c.clinicLinkId === this.newUser.clinic
      );
      if (this.newUser.password === "NOT_PROVIDED") {
        this.showPopup("Password cannot be NOT_PROVIDED", "error");
        return;
      }
      let passwordValue = this.newUser.password;
      if (!passwordValue || passwordValue.trim() === "") {
        // passwordValue = clinic ? clinic.name : ""
        passwordValue = "NOT_PROVIDED";
      } else if (passwordValue.length < 6) {
        this.showPopup("Password must be at least 6 characters long.", "error");
        return;
      }

      console.log("this.newUser.resetPassword", this.newUser.resetPassword);
      if (this.newUser.resetPassword && this.newUser.resetPassword.length < 6) {
        this.showPopup(
          "Reset Password must be at least 6 characters long.",
          "error"
        );
        return;
      } else if (
        this.newUser.resetPassword &&
        this.newUser.resetPassword.length >= 6
      ) {
        if (this.newUser.resetPassword === "NOT_PROVIDED") {
          this.showPopup("Reset Password cannot be NOT_PROVIDED", "error");
          return;
        }
        this.newUser.reset = true;
        passwordValue = this.newUser.resetPassword;
      }
      // const payload = {
      //   email: this.newUser.email,
      //   password: passwordValue,
      //   link: this.newUser.clinic.trim(), //production
      //   // linkId: this.newUser.clinic,
      //   clinicName: clinic ? clinic.name : null,
      //   reset: this.newUser.reset || false
      // }
      // console.log("payload",payload)
      // Prepare payload for the new API
      const payload = {
        user: {
          email: this.newUser.email,
          role: ["user"],
          password: passwordValue,
          link: this.newUser.clinic.trim(), //production
          // linkId: this.newUser.clinic,
          url: window.location.origin,
          clinicName: clinic ? clinic.name : null,
          reset: this.newUser.reset || false,
          resetPasswordRequired: false,
          isDepartment: this.isDepartmentUser,
        },
        mappings: this.categoryRoles.filter((cr) => cr.category !== null).flatMap((cr) => cr.roles),
      };
      try {
        this.isSaving = true;
        //await this.$store.dispatch("signupUser", payload)
        await this.$store.dispatch("signupWithMappings", payload);
        await this.$store.dispatch("fetchAllUsers");
        if (this.selectedClinic) {
          await this.fetchUsersByClinic(this.selectedClinic);
        }
        this.isSaving = false;
        this.showPopup("User created successfully!", "success");
        // reset form
        this.newUser = { email: "", password: "", clinic: "" };
        this.categoryRoles = [
          {
            category: null,
            roles: [],
            open: false,
            rolesOpen: false,
          },
        ];
        this.showModal = false;
      } catch (err) {
        let errorMessage = "Failed to create user!";

        if (err.response?.data) {
          const backendError = err.response.data;
          if (
            typeof backendError === "object" &&
            !Array.isArray(backendError)
          ) {
            errorMessage = Object.values(backendError).join("\n");
          } else if (backendError.message) {
            errorMessage = backendError.message;
          }
        }
        this.showPopup(errorMessage, "error");
      } finally {
        this.isSaving = false;
      }
    },

    handleCSVUpload(event) {
      this.userSaved = false;
      this.invalidEmails = [];
      this.showInvalidEmails = false;
      const file = event.target.files[0];
      if (!file) return;
      const allowedTypes = ["text/csv", "application/vnd.ms-excel"];

      const allowedExtensions = [".csv", ".xls", ".xlsx"];
      const isValidType = allowedTypes.includes(file.type);
      const isValidExtension = allowedExtensions.some((ext) =>
        file.name.toLowerCase().endsWith(ext)
      );
      if (!isValidType && !isValidExtension) {
        this.showPopup("Only CSV or Excel files are allowed!", "error");
        event.target.value = "";
        this.bulkUser.csvFile = null;
        return;
      }
      this.bulkUser.csvFile = file;
      this.bulkUser.csvFileName = file.name;
    },

    togglePassword() {
      this.showPassword = !this.showPassword;
    },

    selectClinic(clinic) {
      this.selectedClinic = clinic.clinicLinkId;
      this.newUser.clinic = clinic.clinicLinkId;
      this.bulkUser.clinic = clinic.clinicLinkId;
      this.clinicSearch = clinic.name;
      this.openmain = false;
      this.open = false;
      this.userSaved = false;
    },

    handleClickOutside(e) {
      const mainDropdown = this.$refs.mainclinicDropdown;

      if (mainDropdown && this.openmain && !mainDropdown.contains(e.target)) {
        this.openmain = false;
        this.open = false;
      }

      // Handle category dropdowns for add user
      this.categoryRoles.forEach((_, index) => {
        const categoryDropdown = this.$refs[`categoryDropdown-${index}`];
        if (
          categoryDropdown &&
          categoryDropdown[0] &&
          this.categoryRoles[index].open &&
          !categoryDropdown[0].contains(e.target)
        ) {
          this.categoryRoles[index].open = false;
        }
      });

      // Handle roles dropdowns for add user
      this.categoryRoles.forEach((_, index) => {
        const rolesDropdown = this.$refs[`rolesDropdown-${index}`];
        if (
          rolesDropdown &&
          rolesDropdown[0] &&
          this.categoryRoles[index].rolesOpen &&
          !rolesDropdown[0].contains(e.target)
        ) {
          this.categoryRoles[index].rolesOpen = false;
        }
      });

      // Handle category dropdowns for user details
      this.userDetailsCategoryRoles.forEach((_, index) => {
        const categoryDropdown =
          this.$refs[`userDetailsCategoryDropdown-${index}`];
        if (
          categoryDropdown &&
          categoryDropdown[0] &&
          this.userDetailsCategoryRoles[index].open &&
          !categoryDropdown[0].contains(e.target)
        ) {
          this.userDetailsCategoryRoles[index].open = false;
        }
      });

      // Handle roles dropdowns for user details
      this.userDetailsCategoryRoles.forEach((_, index) => {
        const rolesDropdown = this.$refs[`userDetailsRolesDropdown-${index}`];
        if (
          rolesDropdown &&
          rolesDropdown[0] &&
          this.userDetailsCategoryRoles[index].rolesOpen &&
          !rolesDropdown[0].contains(e.target)
        ) {
          this.userDetailsCategoryRoles[index].rolesOpen = false;
        }
      });

      // Handle category dropdowns for CSV upload
      this.csvCategoryRoles.forEach((_, index) => {
        const categoryDropdown = this.$refs[`csvCategoryDropdown-${index}`];
        if (
          categoryDropdown &&
          categoryDropdown[0] &&
          this.csvCategoryRoles[index].open &&
          !categoryDropdown[0].contains(e.target)
        ) {
          this.csvCategoryRoles[index].open = false;
        }
      });

      // Handle roles dropdowns for CSV upload
      this.csvCategoryRoles.forEach((_, index) => {
        const rolesDropdown = this.$refs[`csvRolesDropdown-${index}`];
        if (
          rolesDropdown &&
          rolesDropdown[0] &&
          this.csvCategoryRoles[index].rolesOpen &&
          !rolesDropdown[0].contains(e.target)
        ) {
          this.csvCategoryRoles[index].rolesOpen = false;
        }
      });
    },

    toggleSelectAll() {
      if (this.selectAll) {
        this.selectedUsers = this.getUsers.map((user) => user.id);
      } else {
        this.selectedUsers = [];
      }
    },

    deleteUser(user) {
      this.deleteUserData = [
        { linkId: this.selectedClinic, email: user.email },
      ];
      this.showDeleteModal = true;
    },

    openBulkDeleteModal() {
      this.deleteUserData = this.getUsers
        .filter((u) => this.selectedUsers.includes(u.id))
        .map((u) => ({ linkId: this.selectedClinic, email: u.email }));

      this.showDeleteModal = true;
    },

    async confirmDelete() {
      if (this.isDeleting) return;
      this.isDeleting = true;
      try {
        const res = await this.$store.dispatch(
          "deleteUser",
          this.deleteUserData
        );
        this.showPopup(res.message, "success");
        this.showDeleteModal = false;
        this.selectedUsers = [];
        this.deleteUserData = [];
        await this.$store.dispatch("fetchAllUsers");
        if (this.selectedClinic) {
          await this.fetchUsersByClinic(this.selectedClinic);
        }
        // this.selectedUsers = [];
        this.selectAll = false;
      } catch (err) {
        console.error("Delete failed:", err);
        const backendMessage =
          err.response?.data?.message || "Deletion failed. Try again.";
        this.showPopup(backendMessage, "error");
      } finally {
        this.isDeleting = false;
      }
    },

    openBulkResetModal() {
      this.editUserData = { password: "" };
      this.showEditModal = true;
    },

    checkEmail(email, selectedClinicId) {
      if (!email) {
        this.emailExists = false;
        this.emailExistsInSameClinic = false;
        this.listAlreadyRegisteredEmailClinics = [];
        return;
      }

      const normalizedEmail = email.toLowerCase();
      const normalizedClinicId = selectedClinicId.trim().toLowerCase();

      const activeUsers = this.getAllUsers.filter((u) => u.active);

      this.emailExists = activeUsers.some(
        (u) => u.email?.toLowerCase() === normalizedEmail
      );

      let clinicsForThisEmail = [];

      if (this.emailExists) {
        activeUsers.forEach((u) => {
          if (u.email?.toLowerCase() === normalizedEmail) {
            const clinic = this.getClinics.find(
              (c) => c.clinicLinkId === u.clinicLinkId
            );
            if (clinic && !clinicsForThisEmail.includes(clinic.name)) {
              clinicsForThisEmail.push(clinic.name);
            }
          }
        });
      }

      this.listAlreadyRegisteredEmailClinics = clinicsForThisEmail;

      this.showAlreadyRegisteredEmailClinics = true;

      // email exists in the SELECTED clinic
      this.emailExistsInSameClinic = activeUsers.some(
        (u) =>
          u.email?.toLowerCase() === normalizedEmail &&
          u.clinicLinkId?.trim().toLowerCase() === normalizedClinicId
      );
    },
    //categories and roles methods
    deleteCsvCategoryBox(index) {
      this.showConfirmationModal = true;
      this.confirmationMessage =
        "Are you sure you want to delete this category and all its roles?";
      this.deletionContext = "csvUpload";
      this.deletionIndex = index;
    },

    toggleCsvCategoryDropdown(index) {
      this.csvCategoryRoles[index].open = !this.csvCategoryRoles[index].open;
      // Close other category dropdowns
      this.csvCategoryRoles.forEach((cr, i) => {
        if (i !== index) cr.open = false;
      });
    },

    toggleCsvRolesDropdown(index) {
      this.csvCategoryRoles[index].rolesOpen =
        !this.csvCategoryRoles[index].rolesOpen;
      // Close other roles dropdowns
      this.csvCategoryRoles.forEach((cr, i) => {
        if (i !== index) cr.rolesOpen = false;
      });
    },

    getCsvAvailableCategories(index) {
      // Get all selected category IDs except the current one
      const selectedCategoryIds = this.csvCategoryRoles
        .map((cr, i) => (i !== index ? cr.category?.id : null))
        .filter((id) => id !== null && id !== undefined);

      // Return categories that are not already selected
      return this.categories.filter(
        (category) => !selectedCategoryIds.includes(category.id)
      );
    },

    selectCsvCategory(category, index) {
      this.csvCategoryRoles[index].category = category;
      this.csvCategoryRoles[index].open = false;
      // Clear roles when category changes
      this.csvCategoryRoles[index].roles = [];
    },

    removeCsvCategory(index) {
      this.csvCategoryRoles[index].category = null;
      this.csvCategoryRoles[index].roles = [];

      // If this is not the first category, remove it
      if (index > 0) {
        this.csvCategoryRoles.splice(index, 1);
      }
    },

    toggleCsvRole(role, index) {
      const categoryData = this.csvCategoryRoles[index];
      const roleIndex = categoryData.roles.findIndex((r) => r.id === role.id);

      if (roleIndex === -1) {
        categoryData.roles.push(role);
      } else {
        categoryData.roles.splice(roleIndex, 1);
      }
      this.scrollToBottom();
    },

    isCsvRoleSelected(roleId, index) {
      return this.csvCategoryRoles[index].roles.some(
        (role) => role.id === roleId
      );
    },

    removeCsvRole(roleToRemove, index) {
      this.csvCategoryRoles[index].roles = this.csvCategoryRoles[
        index
      ].roles.filter((role) => role.id !== roleToRemove.id);
    },

    addNewCsvCategory() {
      // Check if there are available categories to add
      const availableCategories = this.getCsvAvailableCategories(
        this.csvCategoryRoles.length
      );
      if (availableCategories.length > 0) {
        this.csvCategoryRoles.push({
          category: null,
          roles: [],
          open: false,
          rolesOpen: false,
        });
        this.$nextTick(() => {
          this.scrollToBottom();
        });
      }
    },

    deleteUserDetailsCategoryBox(index) {
      this.showConfirmationModal = true;
      this.confirmationMessage =
        "Are you sure you want to delete this category and all its roles?";
      this.deletionContext = "userDetails";
      this.deletionIndex = index;
    },

    // Method to show confirmation for deleting category in add user
    deleteCategoryBox(index) {
      this.showConfirmationModal = true;
      this.confirmationMessage =
        "Are you sure you want to delete this category and all its roles?";
      this.deletionContext = "addUser";
      this.deletionIndex = index;
    },

    // Method to confirm deletion
    confirmDeletion() {
      if (this.deletionContext === "userDetails") {
        // Remove the category box at the specified index for user details
        this.userDetailsCategoryRoles.splice(this.deletionIndex, 1);

        // If we deleted the last category and there are no categories left, add an empty one
        if (this.userDetailsCategoryRoles.length === 0) {
          this.userDetailsCategoryRoles.push({
            category: null,
            roles: [],
            open: false,
            rolesOpen: false,
          });
        }
      } else if (this.deletionContext === "addUser") {
        // Remove the category box at the specified index for add user
        this.categoryRoles.splice(this.deletionIndex, 1);

        // If we deleted the last category and there are no categories left, add an empty one
        if (this.categoryRoles.length === 0) {
          this.categoryRoles.push({
            category: null,
            roles: [],
            open: false,
            rolesOpen: false,
          });
        }
      } else if (this.deletionContext === "csvUpload") {
        // Remove the category box at the specified index for CSV upload
        this.csvCategoryRoles.splice(this.deletionIndex, 1);

        // If we deleted the last category and there are no categories left, add an empty one
        if (this.csvCategoryRoles.length === 0) {
          this.csvCategoryRoles.push({
            category: null,
            roles: [],
            open: false,
            rolesOpen: false,
          });
        }
      }

      this.closeConfirmationModal();
    },

    // Method to cancel deletion
    cancelDeletion() {
      this.closeConfirmationModal();
    },

    // Method to close confirmation modal
    closeConfirmationModal() {
      this.showConfirmationModal = false;
      this.confirmationMessage = "";
      this.deletionContext = null;
      this.deletionIndex = null;
    },

    async viewUserDetails(user) {
      try {
        console.log(user.email);
        // Fetch user details from API
        const userDetails = await this.$store.dispatch(
          "fetchUserWithMappings",
          user.email
        );

        this.userDetails = {
          email: user.email,
          linkId: this.selectedClinic,
        };
        console.log("API Response:", userDetails);

        // Find the clinic that matches our selected clinic
        const currentClinic = userDetails.clinics.find(
          (clinic) => clinic.clinicLink === this.selectedClinic
        );

        // Transform API response to match our component structure
        if (currentClinic && currentClinic.categories) {
          this.userDetailsCategoryRoles = currentClinic.categories.map(
            (category) => ({
              category: {
                id: category.id,
                name: category.name,
              },
              roles: category.roles || [],
              open: false,
              rolesOpen: false,
            })
          );
        } else {
          // If no categories found for this clinic, initialize empty
          this.userDetailsCategoryRoles = [
            {
              category: null,
              roles: [],
              open: false,
              rolesOpen: false,
            },
          ];
        }

        // Add empty category if none exist
        if (this.userDetailsCategoryRoles.length === 0) {
          this.userDetailsCategoryRoles.push({
            category: null,
            roles: [],
            open: false,
            rolesOpen: false,
          });
        }
        this.originalUserDetailsData = this.userDetailsCategoryRoles.map(
          (cr) => ({
            category: cr.category
              ? { id: cr.category.id, name: cr.category.name }
              : null,
            roles: cr.roles.map((role) => ({ id: role.id, name: role.name })),
          })
        );

        this.hasUserDetailsChanged = false;
        this.showUserDetailsModal = true;
      } catch (error) {
        console.error("Error fetching user details:", error);
        this.showPopup("Failed to fetch user details", "error");
      }
    },

    closeUserDetailsModal() {
      this.showUserDetailsModal = false;
      this.userDetails = {
        email: "",
        linkId: "",
      };
      this.userDetailsCategoryRoles = [];
      this.originalUserDetailsCategoryRoles = [];
      this.hasUserDetailsChanged = false;
    },

    // Methods for handling user details categories and roles
    toggleUserDetailsCategoryDropdown(index) {
      this.userDetailsCategoryRoles[index].open =
        !this.userDetailsCategoryRoles[index].open;
      // Close other category dropdowns
      this.userDetailsCategoryRoles.forEach((cr, i) => {
        if (i !== index) cr.open = false;
      });
    },

    toggleUserDetailsRolesDropdown(index) {
      this.userDetailsCategoryRoles[index].rolesOpen =
        !this.userDetailsCategoryRoles[index].rolesOpen;
      // Close other roles dropdowns
      this.userDetailsCategoryRoles.forEach((cr, i) => {
        if (i !== index) cr.rolesOpen = false;
      });
    },

    getUserDetailsAvailableCategories(index) {
      // Get all selected category IDs except the current one
      const selectedCategoryIds = this.userDetailsCategoryRoles
        .map((cr, i) => (i !== index ? cr.category?.id : null))
        .filter((id) => id !== null && id !== undefined);

      // Return categories that are not already selected
      return this.categories.filter(
        (category) => !selectedCategoryIds.includes(category.id)
      );
    },

    selectUserDetailsCategory(category, index) {
      this.userDetailsCategoryRoles[index].category = category;
      this.userDetailsCategoryRoles[index].open = false;
      // Clear roles when category changes
      this.userDetailsCategoryRoles[index].roles = [];
    },

    removeUserDetailsCategory(index) {
      this.userDetailsCategoryRoles[index].category = null;
      this.userDetailsCategoryRoles[index].roles = [];

      // If this is not the first category, remove it
      if (index > 0) {
        this.userDetailsCategoryRoles.splice(index, 1);
      }
    },

    toggleUserDetailsRole(role, index) {
      const categoryData = this.userDetailsCategoryRoles[index];
      const roleIndex = categoryData.roles.findIndex((r) => r.id === role.id);

      if (roleIndex === -1) {
        categoryData.roles.push(role);
      } else {
        categoryData.roles.splice(roleIndex, 1);
      }
      this.scrollToBottom();
    },

    isUserDetailsRoleSelected(roleId, index) {
      return this.userDetailsCategoryRoles[index].roles.some(
        (role) => role.id === roleId
      );
    },

    removeUserDetailsRole(roleToRemove, index) {
      this.userDetailsCategoryRoles[index].roles =
        this.userDetailsCategoryRoles[index].roles.filter(
          (role) => role.id !== roleToRemove.id
        );
    },

    addNewUserDetailsCategory() {
      // Check if there are available categories to add
      const availableCategories = this.getUserDetailsAvailableCategories(
        this.userDetailsCategoryRoles.length
      );
      if (availableCategories.length > 0) {
        this.userDetailsCategoryRoles.push({
          category: null,
          roles: [],
          open: false,
          rolesOpen: false,
        });
        this.$nextTick(() => {
          this.scrollToBottom();
        });
      }
    },

    async updateUserCategoriesRoles() {
      try {
        // Validation: Check if at least one category is selected
        const hasSelectedCategory = this.userDetailsCategoryRoles.some(
          cr => cr.category !== null
        );
        
        if (!hasSelectedCategory) {
          this.showPopup("Please select at least one category", "error");
          return;
        }

        // Validation: Check that each selected category has at least one role
        for (const cr of this.userDetailsCategoryRoles) {
          if (cr.category && cr.roles.length === 0) {
            this.showPopup(
              `Please select at least one role for ${cr.category.name}`,
              "error"
            );
            return;
          }
        }

        const payload = [
          {
            email: this.userDetails.email,
            linkId: this.userDetails.linkId,
            mappings: this.userDetailsCategoryRoles
              .filter((cr) => cr.category !== null)
              .flatMap((cr) => cr.roles),
          },
        ];

        await this.$store.dispatch("updateUserCategoriesRoles", payload);
        this.showPopup(
          "User categories and roles updated successfully",
          "success"
        );
        this.closeUserDetailsModal();
      } catch (error) {
        console.error("Error updating user categories:", error);
        this.showPopup("Failed to update user categories", "error");
      }
    },

    scrollToBottom() {
      this.$nextTick(() => {
        setTimeout(() => {
          const modal = this.$refs.addUserModal;
          if (modal) {
            modal.scrollTop = modal.scrollHeight;
            console.log(
              "Scrolling to bottom, scrollHeight:",
              modal.scrollHeight
            );
          } else {
            console.log("Modal ref not found");
          }
        }, 100);
      });
    },
    getRolesByCategory(categoryId) {
      const category = this.categories.find((c) => c.id === categoryId);
      return category ? category.roles : [];
    },

    toggleCategoryDropdown(index) {
      this.categoryRoles[index].open = !this.categoryRoles[index].open;
      // Close other category dropdowns
      this.categoryRoles.forEach((cr, i) => {
        if (i !== index) cr.open = false;
      });
    },

    toggleRolesDropdown(index) {
      this.categoryRoles[index].rolesOpen =
        !this.categoryRoles[index].rolesOpen;
      // Close other roles dropdowns
      this.categoryRoles.forEach((cr, i) => {
        if (i !== index) cr.rolesOpen = false;
      });
    },
    selectCategory(category, index) {
      this.categoryRoles[index].category = category;
      this.categoryRoles[index].open = false;
      // Clear roles when category changes
      this.categoryRoles[index].roles = [];
    },

    removeCategory(index) {
      this.categoryRoles[index].category = null;
      this.categoryRoles[index].roles = [];

      // If this is not the first category, remove it
      if (index > 0) {
        this.categoryRoles.splice(index, 1);
      }
    },

    toggleRole(role, index) {
      const categoryData = this.categoryRoles[index];
      const roleIndex = categoryData.roles.findIndex((r) => r.id === role.id);

      if (roleIndex === -1) {
        categoryData.roles.push(role);
      } else {
        categoryData.roles.splice(roleIndex, 1);
      }
      this.scrollToBottom();
    },

    isRoleSelected(roleId, index) {
      return this.categoryRoles[index].roles.some((role) => role.id === roleId);
    },

    removeRole(roleToRemove, index) {
      this.categoryRoles[index].roles = this.categoryRoles[index].roles.filter(
        (role) => role.id !== roleToRemove.id
      );
    },

    addNewCategory() {
      // Check if there are available categories to add
      const availableCategories = this.getAvailableCategories(
        this.categoryRoles.length
      );
      if (availableCategories.length > 0) {
        this.categoryRoles.push({
          category: null,
          roles: [],
          open: false,
          rolesOpen: false,
        });
        this.$nextTick(() => {
          this.scrollToBottom();
        });
      }
    },
  },
  watch: {
    selectedClinic(newVal) {
      if (newVal) {
        this.fetchUsersByClinic(newVal);
      } else {
        this.$store.commit("SET_USERS", []);
      }
    },
    "newUser.email"(newEmail) {
      this.checkEmail(newEmail, this.newUser.clinic);
    },
    "newUser.clinic"(newClinicId) {
      this.checkEmail(this.newUser.email, newClinicId);
    },
    userDetailsCategoryRoles: {
      handler(newVal) {
        // Helper function to filter and prepare data for comparison
        const prepareDataForComparison = (data) => {
          return data
            .filter((cr) => cr.category !== null) // Only non-empty categories
            .map((cr) => ({
              category: { id: cr.category.id, name: cr.category.name },
              roles: cr.roles
                .map((role) => ({ id: role.id, name: role.name }))
                .sort((a, b) => a.id.localeCompare(b.id)), // Sort for consistent comparison
            }))
            .sort((a, b) => a.category.id.localeCompare(b.category.id)); // Sort categories
        };

        const currentData = prepareDataForComparison(newVal);
        const originalData = prepareDataForComparison(
          this.originalUserDetailsData
        );

        this.hasUserDetailsChanged =
          JSON.stringify(currentData) !== JSON.stringify(originalData);
      },
      deep: true,
    },
  },
};
</script>

<style scoped>
.margin {
  margin: 0.5rem !important;
}
.department-checkbox {
  display: flex;
  width: 20px;
  height: 20px;
  align-items: center;
  accent-color: #3B82F6;
  gap: 5px;
  white-space: nowrap;
}
.action-buttons {
  display: flex;
  gap: 10px;
}
.page-wrapper {
  padding: 20px;
  background-image: url("@/assets/login-bg-desktop.svg");
  background-size: cover;
  background-position: center;
  background-repeat: no-repeat;
  min-height: 100vh;
}
.header-section {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 15px;
}
.filter-section {
  display: flex;
  justify-content: flex-start;
  align-items: center;
  margin-bottom: 15px;
  gap: 10px;
}
.clinic-dropdown {
  padding: 8px 36px 8px 10px;
  width: 250px; 
  font-size: 14px; 
  border: 1px solid #3498db; /* Border color */
  border-radius: 10px; /* Rounded corners */
  background-color: #fff; /* Keep background white */
  cursor: pointer; /* Cursor pointer for dropdown */
  position: relative;
  margin-bottom: 2.5%;
}

.clinic-options {
  scroll-padding-right: 2px;
  position: absolute;
  top: 120%;
  left: 0; /* align with parent left */
  width: 100%;
  /* width: 200px ; */
  background: white;
  border: 1px solid #3498db;
  border-radius: 10px;
  max-height: 140px; /* ~4 items visible */
  overflow-y: auto;
  z-index: 1000;
  scrollbar-width: thin;
  scrollbar-color: #3498db transparent;
}

/* Optional: style scrollbar (works only in WebKit browsers like Chrome/Edge/Safari) */
.clinic-dropdown::-webkit-scrollbar {
  width: 8px;
}
.clinic-dropdown::-webkit-scrollbar-thumb {
  background-color: #888;
  border-radius: 4px;
}
.clinic-option {
  padding: 8px 12px;
}
.search-input {
  padding: 8px;
  border: 1px solid #ddd;
  border-radius: 4px;
  flex: 1;
}
.main-title {
  font-size: 1.8rem;
  font-weight: bold;
}
.add-btn {
  background-color: #3498db;
  border: none;
  color: white;
  padding: 8px 16px;
  font-size: 14px;
  border-radius: 10px;
  cursor: pointer;
}
.add-btn:hover {
  background-color: rgba(0, 97, 138, 1);
}
.table-container {
  height: 60vh;
  background: white;
  border-radius: 8px;
  overflow: auto;
  box-shadow: 0 2px 5px rgba(0, 0, 0, 0.1);
}
.styled-table {
  width: 100%;
  border-collapse: collapse;
}
.styled-table thead {
  position: sticky;
  top: 0;
  background-color: #3498db;
  color: white;
}
.styled-table th,
.styled-table td {
  padding: 12px 15px;
  text-align: left;
  border-bottom: 1px solid #ddd;
}

/* Modal */
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.4);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
}
.modal {
  background: #fff;
  padding: 20px;
  border-radius: 8px;
  width: 420px;
  max-height: 80vh;
  overflow-y: auto;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
  padding-bottom: 30px;
  display: flex;
  flex-direction: column;
  max-height: 90vh;
  position: relative;
}
.modal-close-btn {
  position: absolute;
  top: 12px;
  right: 12px;

  /* Makes it a square button */
  width: 32px;
  height: 32px;

  background: #e74c3c;
  border: 1px solid #dcdcdc;
  border-radius: 4px;

  display: flex;
  align-items: center;
  justify-content: center;

  cursor: pointer;
  font-size: 18px;
  color: whitesmoke;
  padding: 0;
}

.modal-close-btn:hover {
  background: #c0392b;
}

.category-options,
.roles-options,
.clinic-options {
  z-index: 1001;
}
.modal::-webkit-scrollbar {
  width: 8px;
}

.modal::-webkit-scrollbar-track {
  background: #f1f1f1;
  border-radius: 4px;
}

.modal::-webkit-scrollbar-thumb {
  background: #c1c1c1;
  border-radius: 4px;
}

.modal::-webkit-scrollbar-thumb:hover {
  background: #a8a8a8;
}
.modal > form {
  flex: 1;
  overflow-y: visible;
  padding-bottom: 20px;
}
.tabs {
  display: flex;
  margin-bottom: 15px;
}
.tabs button {
  flex: 1;
  padding: 10px;
  border: none;
  cursor: pointer;
  background: #eee;
  font-weight: 500;
}
.tabs button.active {
  background: #3498db;
  color: white;
}
.form-group {
  margin-bottom: 12px;
}
.form-group label {
  display: block;
  margin-bottom: 5px;
  font-weight: 500;
}
.form-group input,
.form-group select {
  width: 100%;
  padding: 8px;
  border: 1px solid #ddd;
  border-radius: 4px;
}
/* .actions {
  display: flex;
  justify-content: flex-end;
  margin-top: 15px;
} */
 .actions {
  display: flex;
  justify-content: space-between; /* left & right separation */
  align-items: center;
  width: 100%;
}
.cancel-btn {
  background: #ccc;
  border: none;
  padding: 8px 16px;
  margin-right: 10px;
  border-radius: 4px;
  cursor: pointer;
}
.delete-btn {
  background: #e74c3c;
  border: none;
  color: white;
  padding: 8px 16px;
  border-radius: 4px;
  cursor: pointer;
}
.delete-btn {
  background: #3498db;
  border: none;
  color: white;
  padding: 8px 16px;
  border-radius: 4px;
  cursor: pointer;
}
.delete-btn:hover {
  background: rgba(0, 97, 138, 1);
}
.save-btn {
  background: #3498db;
  border: none;
  color: white;
  padding: 8px 16px;
  border-radius: 4px;
  cursor: pointer;
}
.save-btn:disabled {
  /* background-color: rgb(53, 53, 53); */
  /* background-color: #e6f0ff; */
  cursor: not-allowed;
  opacity: 0.7;
  /* color: #0c5460; */
  border-radius: 1px solid #0c5460;
}
.save-btn:hover {
  background: rgba(0, 97, 138, 1);
}

.update-btn {
  background: #3498db;
  border: none;
  color: white;
  padding: 8px 16px;
  border-radius: 4px;
  cursor: pointer;
}
.update-btn:hover {
  background: rgba(0, 97, 138, 1);
}
.update-btn:disabled {
  /* background-color: rgb(53, 53, 53); */
  /* background-color: #e6f0ff; */
  /* color: #0c5460; */
  cursor: not-allowed;
  opacity: 0.7;
  border-radius: 1px solid #0c5460;
}
.csv-upload p {
  margin-bottom: 10px;
  font-size: 14px;
}
.popup-modal {
  width: 100%;
  display: flex;
  justify-content: space-between;
  align-items: center;
  justify-content: center;
  align-content: center;
  height: 100vh;
}
.popup {
  position: fixed;
  top: 20%;
  padding: 12px 20px;
  border-radius: 8px;
  font-weight: bold;
  color: white;
  z-index: 9999;
  animation: fadeIn 0.3s ease-out;
  display: flex;
  justify-content: center;
  align-items: center;
}

.popup.success {
  background-color: #5cb85c;
}

.popup.error {
  background-color: #f44336;
}

@keyframes fadeIn {
  from {
    opacity: 0;
    transform: translateY(-10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}
.input-wrapper {
  position: relative;
  display: inline-block;
  width: 100%;
}

.password-input {
  width: 100%;
  padding: 10px 40px 10px 12px; /* space for eye on right */
  border: 1px solid #ccc;
  border-radius: 8px;
  box-sizing: border-box;
}

.toggle-password {
  position: absolute;
  right: 12px;
  top: 75%;
  transform: translateY(-50%);
  cursor: pointer;
  color: grey;
  font-size: 18px;
}

.email {
  position: absolute;
  right: 12px;
  top: 75%;
  transform: translateY(-50%);
  color: grey;
  font-size: 18px;
}

.action-icon {
  cursor: pointer;
  margin-right: 20px;
  font-size: 16px;
}
.action-icon.edit {
  color: #3498db;
}
.action-icon.delete {
  color: #e74c3c;
}
.action-icon:hover {
  opacity: 0.7;
}
.action-icon.disabled {
  opacity: 0.7;
  cursor: not-allowed;
  pointer-events: none;
}
/* Container to align icons */
.action-icons {
  display: flex;
  justify-content: flex-start; 
  align-items: center;
  gap: 12px; /* controls spacing between icons */
}

/* Adjust spacing for small screens */
@media (max-width: 768px) {
  .action-icons {
    justify-content: flex-start;
    gap: 8px;
  }
  .clinic-dropdown{
    width: 100%;
  }

  .action-icon {
    margin-right: 0; /* remove large margins on smaller screens */
    font-size: 14px;
  }
}

@media (max-width: 480px) {
  .action-icons {
    flex-wrap: wrap; /* allow wrapping if very narrow */
    justify-content: center;
  }
}



.clinic-input {
  width: 100%;
  border: none;
  padding-right: 48px;
  outline: none;
  font-size: 14px;
  background: transparent;
}
.clinic-option.disabled {
  color: #999;
  cursor: default;
}

/* Responsive dropdown */
@media (max-width: 1024px) {
  .clinic-dropdown {
    /* Tablets */
    padding: 8px 36px 8px 10px;
    width: 250px; /* Adjust width as needed */
    font-size: 14px; /* Smaller font size */
    border: 1px solid #3498db; /* Border color */
    border-radius: 10px; /* Rounded corners */
    background-color: #fff; /* Keep background white */
    cursor: pointer; /* Cursor pointer for dropdown */
    position: relative;
  }

  .clinic-options {
    scroll-padding-right: 2px;
    position: absolute;
    top: 120%;
    left: 0; /* align with parent left */
    width: 100%;
    /* width: 200px ; */
    background: white;
    border: 1px solid #3498db;
    border-radius: 10px;
    max-height: 140px; /* ~4 items visible */
    overflow-y: auto;
    z-index: 1000;
    scrollbar-width: thin;
    scrollbar-color: #3498db transparent;
  }
}

@media (max-width: 768px) {
  /* Mobile */
  .clinic-dropdown {
    /* Tablets */
    padding: 8px 36px 8px 10px;
    width: 250px; /* Adjust width as needed */
    font-size: 14px; /* Smaller font size */
    border: 1px solid #3498db; /* Border color */
    border-radius: 10px; /* Rounded corners */
    background-color: #fff; /* Keep background white */
    cursor: pointer; /* Cursor pointer for dropdown */
    position: relative;
  }

  .clinic-options {
    scroll-padding-right: 2px;
    position: absolute;
    top: 120%;
    left: 0; /* align with parent left */
    width: 100%;
    /* width: 200px ; */
    background: white;
    border: 1px solid #3498db;
    border-radius: 10px;
    max-height: 140px; /* ~4 items visible */
    overflow-y: auto;
    z-index: 1000;
    scrollbar-width: thin;
    scrollbar-color: #3498db transparent;
  }
}

.invalid-emails-section {
  margin: 10px 0;
  border: 1px solid #fd7272;
  border-radius: 8px;
  padding: 10px;
  background: #fff5f5;
}

.invalid-header {
  display: flex;
  align-items: center;
  cursor: pointer;
  font-weight: 600;
  color: #c0392b;
}

.info-icon {
  margin-right: 8px;
  font-size: 1.2rem;
  color: #e74c3c;
}

.toggle-text {
  user-select: none;
}

.invalid-list-box {
  margin-top: 10px;
  padding: 8px;
  border-top: 1px solid #f1c0c0;
}

.invalid-subtitle {
  font-size: 0.9rem;
  margin-bottom: 8px;
  color: #555;
}

.invalid-emails-container {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  max-height: 5rem;
  overflow: auto;
}

.invalid-email-chip {
  background: #ffe0e0;
  border: 1px solid #fd7272;
  padding: 4px 8px;
  border-radius: 20px;
  font-size: 0.85rem;
  color: #a93226;
  white-space: nowrap;
}

.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.3s;
}
.fade-enter,
.fade-leave-to {
  opacity: 0;
}
.existing-emails-section {
  margin-top: 1rem;
  border: 1px solid #74c0fc;
  border-radius: 8px;
  padding: 0.5rem;
  /* max-height: 100px;
  overflow: auto; */
}

.existing-header {
  display: flex;
  align-items: center;
  cursor: pointer;
}

.info-icon {
  margin-right: 0.5rem;
  color: #74c0fc;
}

.existing-list-box {
  margin-top: 0.5rem;
  padding: 0.5rem;
  background: #f8faff;
  border-radius: 6px;
}

.existing-email-chip {
  display: inline-block;
  margin: 0.25rem;
  padding: 0.25rem 0.5rem;
  background: #e6f0ff;
  border: 1px solid #74c0fc;
  border-radius: 12px;
  font-size: 0.85rem;
}

.same-clinic-emails-section {
  margin-bottom: 1rem;
}

.same-clinic-header {
  display: flex;
  align-items: center;
  cursor: pointer;
  background-color: #fff3cd;
  border: 1px solid #ffeeba;
  padding: 8px 12px;
  border-radius: 6px;
  font-weight: 600;
  color: #856404;
}

.warning-icon {
  color: #856404;
  margin-right: 8px;
}

.same-clinic-list-box {
  background: #fffaf0;
  border-left: 4px solid #ffc107;
  margin-top: 6px;
  padding: 10px;
  border-radius: 4px;
}

.same-clinic-email-chip {
  display: inline-block;
  background-color: #ffeeba;
  color: #856404;
  padding: 4px 10px;
  margin: 4px;
  border-radius: 12px;
  font-size: 13px;
  font-weight: 500;
}

.same-clinic-emails-container {
  max-height: 5rem;
  overflow: auto;
}
.error-msg {
  color: red;
}
.warning-msg {
  color: #856404;
}

.registered-email-clinics-section {
  margin-bottom: 1rem;
}

.registered-email-clinics-header {
  display: flex;
  align-items: center;
  cursor: pointer;
  background-color: #e6f7ff;
  border: 1px solid #74c0fc;
  padding: 8px 12px;
  border-radius: 6px;
  font-weight: 600;
  color: #0c5460;
  max-height: 5rem;
  overflow: auto;
}

.registered-clinics-list-box {
  background: #f8faff;
  border-left: 4px solid #74c0fc;
  margin-top: 6px;
  padding: 10px;
  border-radius: 4px;
}

.registered-clinics-container {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  max-height: 5rem;
  overflow: auto;
}

.registered-clinic-email {
  display: inline-block;
  background-color: #e6f0ff;
  color: #0c5460;
  padding: 4px 10px;
  margin: 4px;
  border-radius: 12px;
  font-size: 13px;
  font-weight: 500;
}

.bulk-action-btn {
  background-color: #3498db;
  gap: 2.5%;
  border: none;
  color: white;
  padding: 8px 16px;
  font-size: 14px;
  border-radius: 10px;
  cursor: pointer;
  margin-bottom: 2.5%;
  margin-right: 2.5%;
}
.bulk-action-btn:hover {
  background-color: rgba(0, 97, 138, 1);
}
.bulk-action-btn:disabled {
  background-color: #3498db;
  opacity: 0.7;
  cursor: not-allowed;
  pointer-events: none;
}

.registered-clinics {
  font-size: 14px;
  font-weight: 600;
  margin-bottom: 8px;
  color: #0c5460;
}

.existing-emails-container {
  max-height: 5rem;
  overflow: auto;
}
/*categories and roles */
.category-form-group {
  margin-bottom: 20px;
  width: 100%;
}

.category-form-group label {
  display: block;
  margin-bottom: 6px;
  font-weight: 500;
  color: #374151;
  font-size: 14px;
}

.category-dropdown {
  position: relative;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  padding: 10px 14px;
  cursor: pointer;
  background: white;
  margin-bottom: 8px;
}

.category-selected {
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: 14px;
  color: #374151;
}

.category-selected i {
  color: #9ca3af;
  font-size: 12px;
}

.category-options {
  scroll-padding-right: 2px;
  position: absolute;
  top: 120%;
  left: 0;
  width: 100%;
  /* width: 200px ; */
  background: white;
  border: 1px solid #3498db;
  border-radius: 10px;
  max-height: 140px;
  overflow-y: auto;
  z-index: 1000;
  scrollbar-width: thin;
  scrollbar-color: #3498db transparent;
  margin-bottom: 2px;
}

.category-option {
  padding: 10px 14px;
  cursor: pointer;
  font-size: 14px;
  color: #374151;
  transition: background-color 0.2s;
}

.category-option:hover {
  background: #f3f4f6;
}

/* Roles Dropdown Styles */
.roles-form-group {
  margin-bottom: 20px;
  width: 100%;
}

.roles-form-group label {
  display: block;
  margin-bottom: 6px;
  font-weight: 500;
  color: #374151;
  font-size: 14px;
}

.roles-dropdown {
  position: relative;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  padding: 10px 14px;
  cursor: pointer;
  background: white;
  margin-bottom: 8px;
}

.roles-selected {
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: 14px;
  color: #374151;
}

.roles-selected i {
  color: #9ca3af;
  font-size: 12px;
}

.roles-options {
  scroll-padding-right: 2px;
  position: absolute;
  top: 120%;
  left: 0;
  width: 100%;
  /* width: 200px ; */
  background: white;
  border: 1px solid #3498db;
  border-radius: 10px;
  max-height: 140px;
  overflow-y: auto;
  z-index: 1000;
  scrollbar-width: thin;
  scrollbar-color: #3498db transparent;
}

.roles-option {
  padding: 10px 14px;
  cursor: pointer;
  font-size: 14px;
  color: #374151;
  transition: background-color 0.2s;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
}

.roles-option:hover {
  background: #f3f4f6;
}

.role-name {
  flex: 1;
  word-break: break-word;
  white-space: normal;
}

.role-checkbox {
  margin: 0;
  width: 16px;
  height: 16px;
  accent-color: #3b82f6;
  flex-shrink: 0;
}

.selected-tags-container {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 8px;
}

.selected-tag {
  display: inline-flex;
  align-items: center;
  background: #e5e7eb;
  padding: 6px 10px;
  border-radius: 20px;
  font-size: 13px;
  color: #374151;
  gap: 6px;
}

.remove-tag {
  cursor: pointer;
  color: #6b7280;
  font-size: 12px;
}

.remove-tag:hover {
  color: #ef4444;
}

.form-group {
  margin-bottom: 20px;
}

.form-group label {
  display: block;
  margin-bottom: 6px;
  font-weight: 500;
  color: #374151;
  font-size: 14px;
}
.category-roles-section {
  position: relative;
  padding: 15px;
  margin-bottom: 20px;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  background-color: #fafafa;
}

.add-category-btn-container {
  margin-top: 15px;
  text-align: center;
}

.add-category-btn {
  background: #3b82f6;
  color: white;
  border: none;
  padding: 8px 16px;
  border-radius: 6px;
  cursor: pointer;
  font-size: 14px;
  transition: background-color 0.2s;
}

.add-category-btn:hover {
  background: #2563eb;
}
.category-options,
.roles-options {
  margin-bottom: 10px;
  max-height: 120px;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
}

.action-icon.view {
  color: #4caf50;
  /* margin-left: 8px; */
}

.action-icon.view:hover:not(.disabled) {
  color: #3e8e41;
  transform: scale(1.1);
}

.action-icon.view.disabled {
  color: #ccc;
  cursor: not-allowed;
}

.user-details-section {
  margin-bottom: 20px;
  padding: 15px;
  background-color: #f9f9f9;
  border-radius: 5px;
}

.detail-row {
  display: flex;
  margin-bottom: 10px;
}

.detail-label {
  font-weight: bold;
  width: 80px;
  flex-shrink: 0;
}

.detail-value {
  color: #555;
}

.categories-roles-section {
  margin-bottom: 20px;
}

.categories-roles-section h3 {
  margin-bottom: 15px;
  color: #333;
  border-bottom: 1px solid #eee;
  padding-bottom: 8px;
}
.delete-category-icon {
  position: absolute;
  top: 10px;
  right: 10px;
  color: #73757a;
  cursor: pointer;
  font-size: 14px;
  padding: 5px;
  border-radius: 50%;
  transition: background-color 0.2s;
}

.delete-category-icon:hover {
  background-color: #e2e4e9;
}

.bulk-actions {
  display: flex;
  align-items: center;
  gap: 10px;
  /* margin-bottom: 20px; */
  flex-wrap: wrap;
}

.search-container {
  position: relative;
  display: flex;
  align-items: center;
  margin-left: auto;
}

.search-input {
  padding: 8px 16px 8px 40px; /* Added left padding for the icon */
  width: 250px;
  font-size: 14px;
  border: 1px solid #3498db;
  border-radius: 10px;
  background-color: #fff;
  margin-bottom: 6%;
  margin-bottom: 2.5%; 
  height: 38px; 
  box-sizing: border-box;
}

.search-input:focus {
  outline: none;
  border-color: #3b82f6;
  box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
}

.search-icon {
  position: absolute;
  left: 12px;
  color: #9ca3af;
  font-size: 16px;
  pointer-events: none;
  margin-bottom: 4%;
}

/* Responsive adjustments */
@media (max-width: 768px) {
  .bulk-actions {
    /* flex-direction: column; */
    /* align-items: stretch; */
  }

  .search-container {
    margin-left: 0;
    width: 100%;
  }

  .search-input {
    width: 100%;
  }
}
.clinic-search-icon {
  position: absolute;
  right: 10px;
  top: 50%;
  transform: translateY(-50%);
  color: #666;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  height: 24px;
  width: 24px;
  border-radius: 6px;
  transition: background-color 0.15s ease;
}
</style>
